import React, { useState, useEffect, useRef } from "react";
import {
  Row,
  Col,
  Input,
  Card,
  CardBody,
  Form,
  Badge,
  Button,
  Spinner,
} from "reactstrap";
import { fetchData } from "../../services/apiServices";
import { useNavigate } from "react-router-dom";

const LiveSportsList = () => {
  const navigate = useNavigate();
  const [openEvents, setOpenEvents] = useState([]);
  const [proposedEvents, setProposedEvents] = useState([]);
  const [settledEvents, setSettledEvents] = useState([]);
  const [loading, setLoading] = useState({
    open: true,
    proposed: true,
    settled: true,
  });
  const [loadingMore, setLoadingMore] = useState({
    open: false,
    proposed: false,
    settled: false,
  });
  const [categories, setCategories] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  // Pagination state
  const [pagination, setPagination] = useState({
    open: { currentPage: 1, hasMore: true },
    proposed: { currentPage: 1, hasMore: true },
    settled: { currentPage: 1, hasMore: true },
  });

  // Fetch categories with subcategories
  useEffect(() => {
    const getCategories = async () => {
      try {
        const response = await fetchData("api/admin/categories");
        setCategories(response.categories || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    getCategories();
  }, []);

  // Fetch sports events by status
  const fetchSportsEventsByStatus = async (
    status,
    statusType,
    page = 1,
    append = false
  ) => {
    try {
      if (page === 1) {
        setLoading((prev) => ({ ...prev, [statusType]: true }));
      } else {
        setLoadingMore((prev) => ({ ...prev, [statusType]: true }));
      }

      let url = `api/event/admin/events?status=${status}&limit=20&page=${page}&sports=true`;
      if (searchText) {
        url += `&search=${encodeURIComponent(searchText)}`;
      }
      if (selectedCategory) {
        url += `&category=${selectedCategory}`;
      }

      const response = await fetchData(url);
      const newEvents = response.events || [];
      const hasMore =
        response.pagination &&
        response.pagination.currentPage < response.pagination.totalPages;

      switch (statusType) {
        case "open":
          setOpenEvents((prev) =>
            append ? [...prev, ...newEvents] : newEvents
          );
          break;
        case "proposed":
          setProposedEvents((prev) =>
            append ? [...prev, ...newEvents] : newEvents
          );
          break;
        case "settled":
          setSettledEvents((prev) =>
            append ? [...prev, ...newEvents] : newEvents
          );
          break;
      }

      // Update pagination
      setPagination((prev) => ({
        ...prev,
        [statusType]: {
          currentPage: page,
          hasMore: hasMore,
        },
      }));

      setLoading((prev) => ({ ...prev, [statusType]: false }));
      setLoadingMore((prev) => ({ ...prev, [statusType]: false }));
    } catch (error) {
      console.error(`Error fetching ${statusType} sports events:`, error);
      setLoading((prev) => ({ ...prev, [statusType]: false }));
      setLoadingMore((prev) => ({ ...prev, [statusType]: false }));
    }
  };

  // Load more events for infinite scroll
  const loadMoreEvents = (statusType) => {
    const currentPage = pagination[statusType].currentPage;
    const hasMore = pagination[statusType].hasMore;

    if (!loadingMore[statusType] && hasMore) {
      const statusMap = {
        open: "open",
        proposed: "proposed",
        settled: "settled",
      };
      const status = statusMap[statusType] || statusType;
      fetchSportsEventsByStatus(status, statusType, currentPage + 1, true);
    }
  };

  useEffect(() => {
    // Reset pagination when search or category changes
    setPagination({
      open: { currentPage: 1, hasMore: true },
      proposed: { currentPage: 1, hasMore: true },
      settled: { currentPage: 1, hasMore: true },
    });

    fetchSportsEventsByStatus("open", "open");
    fetchSportsEventsByStatus("proposed", "proposed");
    fetchSportsEventsByStatus("settled", "settled");
  }, [searchText, selectedCategory]);

  // Handle card click to navigate to event detail page
  const handleCardClick = (event) => {
    // Pass sports flag and the event object in location state so the edit page
    // can open the editable sports interface and prefill data if needed.
    navigate(`/events/edit/${event._id}`, { state: { sports: true, event } });
  };

  // Handle search input
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
  };

  // Handle category filter change
  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  // Get category and subcategory for an event
  const getCategoryAndSubcategory = (event) => {
    const category = categories.find((cat) => cat._id === event.category);

    if (!category) return { category: null, subcategory: null };

    const subcategory = category.subcategories?.find(
      (subcat) => subcat._id === event.sub_category
    );

    return { category, subcategory };
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  };

  // Get match status badge
  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "ft":
      case "match finished":
        return <Badge color="success">Finished</Badge>;
      case "live":
        return <Badge color="danger">Live</Badge>;
      case "ns":
      case "not started":
        return <Badge color="info">Not Started</Badge>;
      default:
        return <Badge color="secondary">{status || "Unknown"}</Badge>;
    }
  };

  // Safe score rendering - handles different sports formats
  const renderScore = (scores) => {
    if (!scores) return null;

    // Handle baseball/MLB format: {home: {total: 3}, away: {total: 4}}
    if (
      scores.home &&
      scores.away &&
      typeof scores.home === "object" &&
      typeof scores.away === "object" &&
      scores.home.total !== undefined &&
      scores.away.total !== undefined
    ) {
      return `${scores.away.total} - ${scores.home.total}`;
    }

    // Handle basketball/WNBA format: {home: {total: 99}, away: {total: 62}}
    if (
      scores.home &&
      scores.away &&
      typeof scores.home === "object" &&
      typeof scores.away === "object" &&
      scores.home.total !== undefined &&
      scores.away.total !== undefined
    ) {
      return `${scores.away.total} - ${scores.home.total}`;
    }

    // Handle football/soccer format: {home: 2, away: 1}
    if (
      typeof scores.home !== "undefined" &&
      typeof scores.away !== "undefined" &&
      typeof scores.home === "number" &&
      typeof scores.away === "number"
    ) {
      return `${scores.away} - ${scores.home}`;
    }

    // Handle complex score objects with nested total
    if (typeof scores === "object" && scores.total) {
      if (typeof scores.total === "object" && scores.total.home !== undefined) {
        return `${scores.total.away || 0} - ${scores.total.home || 0}`;
      }
      return scores.total;
    }

    return null;
  };

  // Safe halftime score rendering
  const renderHalftimeScore = (scores) => {
    if (!scores?.halftime) return null;

    if (
      typeof scores.halftime === "object" &&
      typeof scores.halftime.home !== "undefined" &&
      typeof scores.halftime.away !== "undefined"
    ) {
      return `(HT: ${scores.halftime.away} - ${scores.halftime.home})`;
    }

    return null;
  };

  // Sport Event Card Component
  const SportEventCard = ({ event, onClick }) => {
    const { subcategory } = getCategoryAndSubcategory(event);
    const cardRef = useRef(null);

    return (
      <Card
        ref={cardRef}
        className="mb-3 shadow-sm border-0"
        style={{ cursor: "pointer", transition: "all 0.2s" }}
        onClick={() => onClick(event)}
        onMouseEnter={() => {
          if (cardRef.current)
            cardRef.current.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={() => {
          if (cardRef.current)
            cardRef.current.style.transform = "translateY(0)";
        }}
      >
        <CardBody className="p-3">
          {/* League and Match Info */}
          <div className="d-flex align-items-center mb-2">
            {subcategory?.image && (
              <img
                src={subcategory.image}
                alt={subcategory.name}
                style={{
                  width: "24px",
                  height: "24px",
                  objectFit: "cover",
                  borderRadius: "4px",
                  marginRight: "8px",
                }}
              />
            )}
            <small className="text-muted fw-bold">
              {subcategory?.name || "Unknown League"}
            </small>
            <div className="ms-auto">
              {getStatusBadge(
                event.game_status?.short || event.game_status?.long
              )}
            </div>
          </div>

          {/* Teams */}
          <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center">
                {event.team1_image && (
                  <img
                    src={event.team1_image}
                    alt={event.team1_name}
                    style={{
                      width: "32px",
                      height: "32px",
                      objectFit: "cover",
                      borderRadius: "4px",
                      marginRight: "10px",
                    }}
                  />
                )}
                <div>
                  <div className="fw-bold">{event.team1_name || "Team 1"}</div>
                  <small className="text-muted">
                    {event.team1_short_name || ""}
                  </small>
                </div>
              </div>
              <div className="text-center">
                {(() => {
                  const score = renderScore(event.scores);
                  return score ? (
                    <div className="fw-bold fs-5">{score}</div>
                  ) : (
                    <small className="text-muted">vs</small>
                  );
                })()}
              </div>
              <div className="d-flex align-items-center">
                <div className="text-end me-2">
                  <div className="fw-bold">{event.team2_name || "Team 2"}</div>
                  <small className="text-muted">
                    {event.team2_short_name || ""}
                  </small>
                </div>
                {event.team2_image && (
                  <img
                    src={event.team2_image}
                    alt={event.team2_name}
                    style={{
                      width: "32px",
                      height: "32px",
                      objectFit: "cover",
                      borderRadius: "4px",
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Match Details */}
          <div className="border-top pt-2">
            <Row className="g-2">
              <Col xs={6}>
                <small className="text-muted d-block">Start Date</small>
                <small className="fw-bold">
                  {formatDate(event.match_start_date || event.game_start_date)}
                </small>
              </Col>
              <Col xs={6}>
                <small className="text-muted d-block">Pool</small>
                <small className="fw-bold text-success">
                  ${event.total_pool_in_usd || 0}
                </small>
              </Col>
            </Row>

            {event.scores && renderHalftimeScore(event.scores) && (
              <Row className="g-2 mt-1">
                <Col xs={6}>
                  <small className="text-muted d-block">Half Time</small>
                  <small>{renderHalftimeScore(event.scores)}</small>
                </Col>
                <Col xs={6}>
                  <small className="text-muted d-block">Markets</small>
                  <small>{event.sub_markets?.length || 0} markets</small>
                </Col>
              </Row>
            )}
          </div>
        </CardBody>
      </Card>
    );
  };

  // Column Component with infinite scroll
  const StatusColumn = ({
    title,
    events,
    loading,
    colorClass,
    statusType,
    hasMore,
    loadingMore,
    onLoadMore,
  }) => {
    // Handle scroll to bottom
    const handleScroll = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (
        scrollHeight - scrollTop === clientHeight &&
        hasMore &&
        !loadingMore
      ) {
        onLoadMore();
      }
    };

    return (
      <Col lg={4} className="mb-4">
        <Card className="h-100">
          <CardBody className="p-0">
            <div className={`p-3 ${colorClass} text-white`}>
              <h5 className="mb-0 d-flex align-items-center justify-content-between">
                {title}
                <Badge color="light" className="text-dark">
                  {events.length}
                </Badge>
              </h5>
            </div>
            <div
              className="p-3"
              style={{ maxHeight: "70vh", overflowY: "auto" }}
              onScroll={handleScroll}
            >
              {loading ? (
                <div className="text-center py-4">
                  <Spinner color="primary" />
                  <div className="mt-2">Loading...</div>
                </div>
              ) : events.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="mdi mdi-inbox-outline fs-1 d-block mb-2"></i>
                  No events found
                </div>
              ) : (
                <>
                  {events
                    .map((event) => {
                      // Safety check - ensure event has required properties
                      if (!event || !event._id) {
                        console.warn("Invalid event data:", event);
                        return null;
                      }

                      return (
                        <SportEventCard
                          key={event._id}
                          event={event}
                          onClick={handleCardClick}
                        />
                      );
                    })
                    .filter(Boolean)}

                  {/* Load more indicator */}
                  {loadingMore && (
                    <div className="text-center py-3">
                      <Spinner size="sm" color="primary" />
                      <small className="ms-2">Loading more...</small>
                    </div>
                  )}

                  {/* Load more button if needed */}
                  {hasMore && !loadingMore && events.length > 0 && (
                    <div className="text-center py-3">
                      <Button
                        color="outline-primary"
                        size="sm"
                        onClick={onLoadMore}
                      >
                        Load More
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardBody>
        </Card>
      </Col>
    );
  };

  return (
    <div>
      {/* Header and Filters */}
      <Card className="mb-4">
        <CardBody>
          <Row className="align-items-center">
            <Col lg="6">
              <h4 className="mb-0">Live Sports Events</h4>
              <p className="text-muted mb-0">
                Manage sports events across different statuses
              </p>
            </Col>
            <Col lg="6">
              <Form>
                <Row className="g-2">
                  <Col>
                    <Input
                      type="search"
                      placeholder="Search sports events..."
                      value={searchText}
                      onChange={handleSearchChange}
                    />
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Three Column Layout */}
      <Row>
        <StatusColumn
          title="Open Events"
          events={openEvents}
          loading={loading.open}
          colorClass="bg-success"
          statusType="open"
          hasMore={pagination.open.hasMore}
          loadingMore={loadingMore.open}
          onLoadMore={() => loadMoreEvents("open")}
        />
        <StatusColumn
          title="Proposed Events"
          events={proposedEvents}
          loading={loading.proposed}
          colorClass="bg-info"
          statusType="proposed"
          hasMore={pagination.proposed.hasMore}
          loadingMore={loadingMore.proposed}
          onLoadMore={() => loadMoreEvents("proposed")}
        />
        <StatusColumn
          title="Settled Events"
          events={settledEvents}
          loading={loading.settled}
          colorClass="bg-secondary"
          statusType="settled"
          hasMore={pagination.settled.hasMore}
          loadingMore={loadingMore.settled}
          onLoadMore={() => loadMoreEvents("settled")}
        />
      </Row>
    </div>
  );
};

export default LiveSportsList;
